import React, { FC } from 'react'
import { BreakdownLedgerContainer } from '../../BreakdownLedger'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getLCDLicensingBreakdownDetailsHeader } from './getLCDLicensingBreakdownDetailsHeaders'
import { DirectorLicensingFeePerRateBreakdown } from '../../../../../swagger'
import LicenseFeeSection from './LicenseFeeSection'
import { useBreakdownData } from './useBreakdownData'
import { LCDLicensingBreakdownPreviewProps } from '../Preview'
import LicenseBalanceSection from './LicenseBalanceSection'
import getLocaleCurrencyForAmount from '../../../../../utils/getLocaleCurrencyForAmount'
import { convertTwoDecimalStringToBig } from '../../../../../utils/bigJSHelper'

interface LCDLicensingBreakdownDetailsProps
  extends Omit<LCDLicensingBreakdownPreviewProps, 'lcdName'> {
  breakdown: DirectorLicensingFeePerRateBreakdown[]
  isChallenge: boolean
}

const LCDLicensingBreakdownDetails: FC<LCDLicensingBreakdownDetailsProps> = ({
  breakdown,
  isChallenge,
  totalOwed,
  totalPaid,
  balance,
  licensingDue,
  currencyCode,
}) => {
  const { t } = useTranslation()

  const theme = useTheme()
  const breakdownHeaders = getLCDLicensingBreakdownDetailsHeader(theme, t)

  const {
    licensingFeeFirstStudent,
    licensingFeeAdditionalStudent,
    licensingFeeSubtotal,
    // 1st semester
    semester1FirstStudent,
    semester1AdditionalStudent,
    semester1Subtotal,
    // 2nd semester
    semester2FirstStudent,
    semester2AdditionalStudent,
    semester2Subtotal,
  } = useBreakdownData({ breakdown })

  return (
    <BreakdownLedgerContainer headers={breakdownHeaders}>
      <>
        {convertTwoDecimalStringToBig(licensingFeeSubtotal).gt(0) && (
          <LicenseFeeSection
            title={t(
              'LCDLicensingBreakdownDetails.LicenseFeeSection.SubHeader.InvitationLicenseFee',
              'Invitation License Fee'
            )}
            rows={[
              {
                label: 'First Students',
                ...licensingFeeFirstStudent,
              },
              {
                label: 'Additional Students',
                ...licensingFeeAdditionalStudent,
              },
            ]}
            subtotal={getLocaleCurrencyForAmount(
              licensingFeeSubtotal,
              currencyCode
            )}
          />
        )}
        {convertTwoDecimalStringToBig(semester1Subtotal).gt(0) && (
          <LicenseFeeSection
            title={
              isChallenge
                ? t(
                    'LCDLicensingBreakdownDetails.LicenseFeeSection.SubHeader.FirstSemesterLicenseFee',
                    '1st Semester License Fee'
                  )
                : t(
                    'LCDLicensingBreakdownDetails.LicenseFeeSection.SubHeader.LicenseFee',
                    'License Fee'
                  )
            }
            rows={[
              {
                label: 'First Students',
                ...semester1FirstStudent,
              },
              {
                label: 'Additional Students',
                ...semester1AdditionalStudent,
              },
            ]}
            subtotal={semester1Subtotal}
          />
        )}

        {isChallenge &&
          convertTwoDecimalStringToBig(semester2Subtotal).gt(0) && (
            <LicenseFeeSection
              title={t(
                'LCDLicensingBreakdownDetails.LicenseFeeSection.SubHeader.SecondSemesterLicenseFee',
                '2nd Semester License Fee'
              )}
              rows={[
                {
                  label: 'First Students',
                  ...semester2FirstStudent,
                },
                {
                  label: 'Additional Students',
                  ...semester2AdditionalStudent,
                },
              ]}
              subtotal={semester2Subtotal}
            />
          )}
        <LicenseBalanceSection
          totalOwed={totalOwed}
          totalPaid={totalPaid}
          balance={balance}
          currencyCode={currencyCode}
          licensingDue={licensingDue}
        />
      </>
    </BreakdownLedgerContainer>
  )
}

export default LCDLicensingBreakdownDetails
